export {};

// Types for Shopify Product Recommendations API (REST/AJAX format)
interface RecVariantImage {
  src: string;
  alt: string | null;
}

interface RecVariant {
  id: number;
  option1: string;
  price: number;
  featured_image: RecVariantImage | null;
}

interface RecProduct {
  id: number;
  title: string;
  handle: string;
  available: boolean;
  variants: RecVariant[];
  tags: string[];
  type: string;
  price: number;
  featured_image: string | null;
  metafield?: { value: string } | null;
}

// Union type for buildImageRec (accepts product or variant)
interface ImageBuildSource {
  featured_image?: RecVariantImage | string | null;
  title?: string;
}

// Local state (original relied on global from another script)
const ymalState = {
  currentLanguage: document.documentElement.lang.toUpperCase()
};

// ---------------------------------------------------------------------------
// Money format handler
// ---------------------------------------------------------------------------
Shopify.money_format = "${{amount}}";
Shopify.formatMoney = function(cents: number | string, format?: string): string {
  if (typeof cents == 'string') { cents = cents.replace('.',''); }
  let value: string | number = '';
  const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
  const formatString = (format || Shopify.money_format);

  function defaultOption<T>(opt: T | undefined, def: T): T {
     return (typeof opt == 'undefined' ? def : opt);
  }

  function formatWithDelimiters(number: number | string | null, precision?: number, thousands?: string, decimal?: string): string | number {
    precision = defaultOption(precision, 2);
    thousands = defaultOption(thousands, ',');
    decimal   = defaultOption(decimal, '.');

    if (isNaN(number as number) || number == null) { return 0; }

    const fixed = (Number(number)/100.0).toFixed(precision);

    const parts   = fixed.split('.'),
        dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands),
        centsStr   = parts[1] ? (decimal + parts[1]) : '';

    return dollars + centsStr;
  }

  const match = formatString.match(placeholderRegex);
  if (!match) return formatString;

  switch(match[1]) {
    case 'amount':
      value = formatWithDelimiters(cents as number, 2);
      break;
    case 'amount_no_decimals':
      value = formatWithDelimiters(cents as number, 0);
      break;
    case 'amount_with_comma_separator':
      value = formatWithDelimiters(cents as number, 2, '.', ',');
      break;
    case 'amount_no_decimals_with_comma_separator':
      value = formatWithDelimiters(cents as number, 0, '.', ',');
      break;
  }

  return formatString.replace(placeholderRegex, String(value));
};

function buildImageRec(currentProduct: ImageBuildSource, fallback?: RecProduct): string {
    const fi = currentProduct.featured_image;
    let imageurl: string | undefined;
    let imagealt: string | undefined;

    if (fi && typeof fi === 'object') {
      imageurl = fi.src;
      imagealt = fi.alt ?? undefined;
    }
    if (!imageurl) {
      imageurl = typeof fi === 'string' ? fi : undefined;
      imagealt = currentProduct.title;
    }

    // Fall back to the product's featured_image when the variant has none
    if (!imageurl && fallback?.featured_image) {
      const fallbackFi = fallback.featured_image;
      imageurl = typeof fallbackFi === 'string' ? fallbackFi : fallbackFi?.src;
      imagealt = fallback.title;
    }

    let imageSection = '';
    if (imageurl == '' || imageurl == undefined) {
      imageSection = `<div class="card__media">
          <div class="media media--transparent media--hover-effect --missing-image">
            <img src="${window.themeConfig?.assets?.loadingSpinner ?? ''}" alt="loading" width="75" height="75">
          </div>
      </div>`;
    } else {
      imageSection = `<div class="card__media">
          <div class="media media--transparent media--hover-effect">
            <img
              src="${imageurl}&width=220"
              srcset="${imageurl}&width=130 130w, ${imageurl}&width=180 180w, ${imageurl}&width=220 220w, ${imageurl}&width=350 350w, ${imageurl}&width=440 440w"
              sizes="(min-width: 990px) 220px, (min-width: 500px) 180px, 130px"
              alt="${imagealt}"
              loading="lazy">
          </div>
      </div>`;
    }
    return imageSection;
  }

  // Fonction pour construire le HTML d'un produit
  function buildProductRecHtml(product: RecProduct, section: HTMLElement): string {
    const { id, title, handle, available, variants, tags, type, price, metafield } = product;
    const currentVariant = section.dataset.currentVariant;
    const variantBySelectedOptions = variants.find(variant => {
      return variant.option1 == currentVariant
    });
    let variantId = variantBySelectedOptions ? variantBySelectedOptions.id : null;
    let productUrl = handle;
    if(variantId) {
        productUrl = productUrl + '?variant=' + variantId;
    }

    let new_title = title;

    if (new_title.indexOf("Clear Case") >= 0) {
      new_title = new_title.substring(0, new_title.indexOf("Clear Case"));
    }
    if (new_title.indexOf("AirPods Case") >= 0) {
      new_title = new_title.substring(0, new_title.indexOf("AirPods Case"));
    }
    if (new_title.indexOf(" iPad Folio") >= 0) {
      new_title = new_title.substring(0, new_title.indexOf(" iPad Folio"));
    }
    if (new_title.indexOf("MacBook skin") >= 0) {
      new_title = new_title.substring(0, new_title.indexOf("MacBook skin"));
    }
    if (new_title.indexOf(" Folio Case") >= 0) {
      new_title = new_title.substring(0, new_title.indexOf(" Folio Case"));
    }
    if (new_title.indexOf(" Kindle Case") >= 0) {
      new_title = new_title.substring(0, new_title.indexOf(" Kindle Case"));
    }
    if (new_title.indexOf(" étui pour liseuse") >= 0) {
      new_title = new_title.substring(0, new_title.indexOf(" étui pour liseuse"));
    }
    const lang = document.documentElement.lang.toUpperCase();

    const urlPrefix = lang == 'FR' ? '/fr' : '';

    let oosBadge = '';
    let lowStockBadge = '';
    let newBadge = '';
    let colorLabel = '';
    let finalSaleBadge = '';
    const newTag = tags.filter(tag => {
      return tag == 'New'
    });
    const finalSaleTag = tags.filter(tag => {
      return tag == 'final_sale'
    });

    const hasForceBadge = document.body.dataset.forceBadge;
    let forceBadge = '';
    const hasForceBadgeCondition = hasForceBadge == 'true';

    if(hasForceBadgeCondition && document.body.dataset.forceBadgeType?.includes(type)) {
      forceBadge = `<span class="badge badge--${document.body.dataset.forceBadgeColor}">${document.body.dataset.forceBadgeText}</span>`;
    }

    if(!available) {
      oosBadge = ymalState.currentLanguage == 'FR' ? '<span class="badge">Épuisé</span>':'<span class="badge">Out of Stock</span>';
    } else {
      const lowTag = tags.filter(tag => {
        return tag == 'low-stock'
      });
      if(lowTag.length > 0) {
        lowStockBadge = ymalState.currentLanguage == 'FR' ? '<span class="badge">Bientôt épuisé</span>':'<span class="badge">Low Stock</span>';
      }
    }

    if(newTag.length > 0) {
      newBadge = ymalState.currentLanguage == 'FR' ? '<span class="badge">Nouveau</span>':'<span class="badge">New</span>';
    }

    if(finalSaleTag.length > 0) {
      finalSaleBadge = ymalState.currentLanguage == 'FR' ? '<span class="badge">Vente finale</span>':'<span class="badge">Final Sale</span>';
    }

    const quickAddLabel = lang == 'FR' ? 'Ajout rapide':'Quick Add';
    if(metafield && metafield.value) {
      if (lang == 'FR') {
        colorLabel = `<div class="card__label">Plus de couleurs disponibles</div>`;
      } else {
        colorLabel = `<div class="card__label">More colors available</div>`;
      }
    }
    const priceToShow = price;
    let priceFormatted: string;
    if(product.type == 'ChargePods') {
      priceFormatted = Shopify.formatMoney(7999, '{{amount}}');
    } else {
      priceFormatted = Shopify.formatMoney(variantBySelectedOptions ? variantBySelectedOptions.price : priceToShow, '{{amount}}');
    }
    // FIX: original was `priceFormatted.replace("$","")` without assignment (no-op)
    priceFormatted = priceFormatted.replace("$","");

    const enPriceTag = document.documentElement.lang.toUpperCase() == 'EN' ? '$' : '';
    const frPriceTag = document.documentElement.lang.toUpperCase() == 'FR' ? '$' : '';
    priceFormatted = enPriceTag + priceFormatted + frPriceTag;

    const quickAdd = `<div class="quick-add no-js-hidden">
        <modal-opener data-modal="#QuickAdd-${id}">
          <button id="quick-add-template--${id}-submit"
            type="submit" name="add"
            class="quick-add__submit button button--full-width button--outline" aria-haspopup="dialog"
            aria-labelledby="quick-add-template--${id} title-template--${id}"
            data-product-url="${urlPrefix}/products/${productUrl}">
            ${quickAddLabel}
            <div class="loading__spinner hidden">
              <img src="${window.themeConfig?.assets?.loadingSpinner ?? ''}" alt="loading" width="75" height="75">
            </div>
          </button>
        </modal-opener>
        <quick-add-modal id="QuickAdd-${id}" class="quick-add-modal">
          <div
            role="dialog"
            aria-label="${new_title}"
            aria-modal="true"
            class="quick-add-modal__content global-settings-popup"
            tabindex="-1"
          >
            <button
              id="ModalClose-${id}"
              type="button"
              class="quick-add-modal__toggle button button--outline"
              aria-label="Close"
            >
              <svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" role="presentation" class="icon icon-close" fill="none" viewBox="0 0 18 17">
                <path d="M.865 15.978a.5.5 0 00.707.707l7.433-7.431 7.579 7.282a.501.501 0 00.846-.37.5.5 0 00-.153-.351L9.712 8.546l7.417-7.416a.5.5 0 10-.707-.708L8.991 7.853 1.413.573a.5.5 0 10-.693.72l7.563 7.268-7.418 7.417z" fill="currentColor">
              </svg>
            </button>
            <div id="QuickAddInfo-${id}" class="quick-add-modal__content-info"></div>
          </div>
        </quick-add-modal>
      </div>`;

    return `
      <li class="grid__item">
        <div class="card-wrapper product-card-wrapper underline-links-hover">
          <a href="${urlPrefix}/products/${productUrl}">
            <span class="visually-hidden">${new_title}</span>
          </a>
          <div class="card card--standard card--media">
            <div class="card__inner">
              ${buildImageRec(variantBySelectedOptions ?? product, product)}
            </div>
            <div class="card__content">
              <div class="card__information">
                <h3 class="card__heading">
                  <a href="${productUrl}" class="full-unstyled-link">
                    ${new_title}
                  </a>
                </h3>
                <div class="card-information">
                  <div class="price">
                    <div class="price__container">
                      <div class="price__regular">
                        ${priceFormatted}
                      </div>
                    </div>
                  </div>
                  ${colorLabel}
                </div>
              </div>
              <!--${quickAdd}-->
            </div>
            <div class="card__badge top left">
              ${newBadge}
              ${lowStockBadge}
              ${forceBadge}
              ${finalSaleBadge}
            </div>
          </div>
        </div>
      </li>
    `;
  }


  // Fonction de fetch principale
  async function fetchProductrecommendation(productId: string | undefined, section: HTMLElement): Promise<void | false> {
    try {
      const limit = parseInt(section.dataset.limit || '4');
      const fetchLimit = Math.min(limit + 4, 10);
      const response = await fetch(Shopify.routes.root + `recommendations/products.json?product_id=${productId}&limit=${fetchLimit}&intent=related`);
      if (!response.ok) throw new Error('Network response was not ok');
      const data = await response.json();

      const products: RecProduct[] = (data && data.products) ? data.products : [];
      const filteredProducts = products.filter((product) => {
        if (!product) return false;
        const productType = (product.type || '').toString().trim().toLowerCase();
        if (productType.indexOf('corpo phone case') !== -1) return false;
        if (product.tags.includes('do_not_recommend')) return false;

        return true;
      }).slice(0, limit);

      if (filteredProducts.length > 0) {
        section.querySelector('ul')!.innerHTML = '';
        let productHTML = '';
        filteredProducts.forEach((product) => {
          productHTML += buildProductRecHtml(product, section);
        });
        section.querySelector('ul')!.innerHTML += productHTML;
      } else {
        const parent = section.parentElement?.parentElement;
        const parentId = parent?.id;
        if (parentId) {
          document.querySelector('[href="#' + parentId + '"]')?.remove();
        }
        parent?.remove();
      }
      return;
    } catch (error) {
      console.error('Error fetching products:', error);
      return false;
    }
  }

  // Initialisation
  document.addEventListener('DOMContentLoaded', () => {
    const section = document.querySelector(`.you-may-also-like .related-products`);
    if (!section) {
      console.warn(`Section not found`);
      return;
    }
    const productId = (section as HTMLElement).dataset.productId;
    fetchProductrecommendation(productId, section as HTMLElement);
  });
